import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { InvoicePlanSaveError } from '../../helpers/invoicePlanErrors';
import {
  calculateCadenceTransitionTotal,
  dollarsToCents,
  validateCadenceTransitionFinancials,
} from '../../helpers/invoicePlans';
import { firestore, functions } from './firebase';
import {
  createQuerySnapshot,
  deleteDocument,
  getDocument,
  getDocuments,
  getDocumentsAsArray,
  setDocument,
  startCollectionListener,
  updateDocument,
} from './firestore';

import { InvoicePlanStatus, type Invoice } from '@wonderschool/common-base-types';
import { InvoiceStatus } from '../../helpers/invoices';
import { findLocationIdForStudent, resolveInvoicePlanLocationId } from '../../students/studentsUtils';

import { QueryConditionType } from '../../reports/types';

export const paths = {
  invoice: (organizationId, invoiceId) => `organizations/${organizationId}/invoices/${invoiceId}`,
  invoices: (organizationId) => `organizations/${organizationId}/invoices`,
  invoicePlans: (organizationId) => `organizations/${organizationId}/invoicePlans`,
  invoicePlan: (organizationId, planId) => `organizations/${organizationId}/invoicePlans/${planId}`,
};

const INVOICES_LIMIT = 2000; // We ran into performance issues with providers having 4-5k+ invoices so we decided to cap for now as a bandaid
const INTERVAL_TRANSITION_RECOVERY_HISTORY_LIMIT = 5;

export async function fetchOrganizationInvoices(
  organizationId,
  conditions = [],
  _orderBy = [],
  limit = null,
  startAfter = null
) {
  return getDocuments({
    path: paths.invoices(organizationId),
    conditions,
    // orderBy: [{ field: 'dateDue', direction: 'asc' }, ...orderBy],
    limit,
    startAfter,
  });
}

export async function fetchOrganizationInvoicePlans(
  organizationId,
  conditions: QueryConditionType[] = [],
  orderBy = [],
  limit = null,
  startAfter = null
): Promise<{
  count: number;
  list: {
    id: string;
    deletedAt?: string;
  }[];
}> {
  return getDocumentsAsArray({
    path: paths.invoicePlans(organizationId),
    conditions,
    orderBy: [{ field: 'dateStart', direction: 'desc' }, ...orderBy],
    limit,
    startAfter,
  });
}

export async function fetchOrganizationInvoicesForPlan(organizationId: string, invoicePlanId: string) {
  const result = await getDocumentsAsArray({
    path: paths.invoices(organizationId),
    conditions: [{ field: 'invoicePlanId', operation: '==', value: invoicePlanId }],
  });
  return { ...result, list: result.list as unknown as Invoice[] };
}

/**
 * Fetch an org's LIVE recurring invoice plans, unordered: active, plus paused
 * (resumable — the backend treats active+paused as live), scheduled and send
 * (transient states while an invoice generates). Only `ended` plans are
 * excluded, so double-billing checks can't miss a plan that's momentarily in
 * a transient state. Deliberately NOT built on `fetchOrganizationInvoicePlans`:
 * its baked-in `orderBy dateStart` turns the status filter into a
 * composite-index query that throws without an explicit index. An equality/in
 * filter alone runs on the automatic single-field index.
 *
 * Archived and soft-deleted plans are dropped in memory rather than in the
 * query, because Firestore won't take an inequality alongside the `in` above.
 * They matter because archiving does NOT change `status`:
 * `markRecurringPlansAsArchive` writes only `isArchived`, and
 * `isRecurringPlanEligibleForArchive` admits `ended` OR `paused` plans. `ended`
 * is already excluded here, so the case that leaks is an archived PAUSED plan —
 * without this filter it still reads as "already billed" and silently denies
 * that family a new plan. `deletedAt` is filtered for the same reason
 * (`hooks/useInvoicePlans.ts` already treats it as not-live).
 */
export async function fetchActiveInvoicePlans(
  organizationId: string
): Promise<Array<{ id: string } & Record<string, any>>> {
  const snapshot = await createQuerySnapshot(paths.invoicePlans(organizationId), [
    { field: 'status', operation: 'in', value: ['active', 'paused', 'scheduled', 'send'] },
  ]);
  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((plan: any) => !plan.isArchived && !plan.deletedAt);
}

export const fetchInvoicePlansWithMultipleStudents = async () => {
  const {
    list: invoicePlans,
  }: {
    list: {
      id: string;
      students?: any[];
    }[];
  } = await getDocumentsAsArray({
    path: 'invoicePlans',
    conditions: [
      {
        field: 'status',
        operation: '==',
        value: 'active',
      },
    ],
  });

  return invoicePlans.filter(({ students }) => students?.length && students?.length > 1);
};

export async function fetchOrganizationInvoice(organizationId: string, invoiceId: string) {
  getDocument({
    path: paths.invoice(organizationId, invoiceId),
  });
}

export function organizationInvoicesOnListen(
  {
    organizationId,
    locations,
    limitInvoices,
  }: {
    organizationId: string;
    locations: { [key: string]: boolean };
    limitInvoices: boolean;
  },
  next: (...args: any[]) => void,
  error: (error: Error) => void,
  complete?: () => void
) {
  const conditions: QueryConditionType[] = locations
    ? [
        {
          field: 'location',
          operation: 'in',
          value: Object.keys(locations),
        },
      ]
    : [];

  const queryOptions: any = {
    path: paths.invoices(organizationId),
    conditions,
    orderBy: [{ field: 'dateDue', direction: 'desc' }],
  };

  if (limitInvoices) {
    queryOptions.limit = INVOICES_LIMIT;
  }

  return startCollectionListener(queryOptions, next, error, complete);
}

export function organizationParentInvoicesOnListen(
  {
    organizationId,
    contactId,
  }: {
    organizationId: string;
    contactId: string;
  },
  next: (...args: any[]) => void,
  error: (error: Error) => void,
  complete?: () => void
) {
  const conditions: QueryConditionType[] = [
    {
      field: 'student.familyMembers',
      operation: 'array-contains',
      value: contactId,
    },
  ];

  return startCollectionListener(
    {
      path: paths.invoices(organizationId),
      conditions,
      orderBy: [{ field: 'dateDue', direction: 'asc' }],
    },
    next,
    error,
    complete
  );
}

export async function createOrganizationInvoice(organizationId: string, payload: any) {
  const batch = firestore().batch();
  // Need this object to pass into redux flow on create
  const batchResult = {};

  if (payload && payload.students && payload.students.length) {
    for (const student of payload.students) {
      if (!student) continue;

      const invoiceDocRef = firestore().collection('organizations').doc(organizationId).collection('invoices').doc();

      const locationId = findLocationIdForStudent(student);

      const invoiceData = {
        ...payload.details,
        organization: organizationId,
        location: locationId,
        student,
        createdAt: Date.now(),
        collectionEnrollmentVersion: 1,
      };

      batch.set(invoiceDocRef, invoiceData);
      batchResult[invoiceDocRef.id] = invoiceData;
    }
  }

  await batch.commit();
  return batchResult;
}

export async function createOrganizationInvoicePlan(organizationId, payload) {
  const batch = firestore().batch();
  const batchResult = {};

  if (payload && payload.students && payload.students.length) {
    for (const student of payload.students) {
      if (!student) continue;

      const invoiceDocRef = firestore()
        .collection('organizations')
        .doc(organizationId)
        .collection('invoicePlans')
        .doc();

      const invoicePlan = prepareInvoicePlanForWrite({
        ...payload,
        students: [student],
      });

      batch.set(invoiceDocRef, invoicePlan);

      // Need this object to pass into redux flow on create
      batchResult[invoiceDocRef.id] = invoicePlan;
    }
  }

  await batch.commit();
  return batchResult;
}
const INTERVAL_SCHEDULER_FIELDS = [
  'status',
  'billingInterval',
  'dateDue',
  'dateSend',
  'dateServiceStart',
  'dateServiceEnd',
  'dateEnd',
  'serviceDirection',
  'isServiceDatesEnabled',
  'notificationDays',
  'location',
  'intervalChangePreviousBillingInterval',
  'intervalChangeEffectiveDue',
  'intervalChangeNotificationPending',
  'intervalTransitionAttemptId',
  'intervalTransitionRecovery',
  'intervalTransitionPhase',
] as const;
const SUPPORTED_BILLING_INTERVALS = new Set([
  'daily',
  'weekly',
  'biweekly',
  'bi-weekly',
  'twice per month',
  'monthly',
  'quarterly',
  'yearly',
]);

export const setOrganizationInvoicePlan = async (organizationId, payload) => {
  if (
    payload.intervalChangeExpectedState &&
    (payload.intervalChangePreviousBillingInterval || payload.intervalTransitionRecovery)
  ) {
    return setOrganizationInvoicePlanIntervalAtomically(organizationId, payload);
  }

  const data = prepareInvoicePlanForWrite(payload);
  return setDocument({
    path: `organizations/${organizationId}/invoicePlans/${payload.id}`,
    data,
  });
};

export async function endOrganizationInvoicePlan(
  organizationId: string,
  invoicePlanId: string,
  data: {
    dateEnd: number;
    status: string;
    endPlanSource: 'provider';
    endPlanNotificationFailure?: null;
    endPlanNotification?: {
      subject: string;
      message: string;
      chargeAmount: number;
      invoiceCount: number;
      requestedAt: number;
    } | null;
  }
) {
  if (!invoicePlanId) throw Error('Invoice plan ID is required');

  const invoicePlanRef = firestore().doc(paths.invoicePlan(organizationId, invoicePlanId));
  await firestore().runTransaction(async (transaction) => {
    const currentSnapshot = await transaction.get(invoicePlanRef);
    const currentStatus = currentSnapshot.data()?.status;

    if (![InvoicePlanStatus.ACTIVE, InvoicePlanStatus.PAUSED].includes(currentStatus)) {
      throw new Error('Invoice plan changed while ending. Refresh and try again.');
    }

    transaction.update(invoicePlanRef, {
      ...data,
      status: data.status === InvoicePlanStatus.ENDED ? InvoicePlanStatus.ENDED : currentStatus,
      endPlanNotificationFailure: null,
      updatedAt: Date.now(),
    });
  });
}

async function setOrganizationInvoicePlanIntervalAtomically(organizationId, payload) {
  const invoiceItemList = payload.invoiceItemList ?? payload.invoiceItems ?? [];
  const recomputedTotal = validateIntervalTransitionFinancialPayload(invoiceItemList, payload.total);
  const planRef = firestore().doc(paths.invoicePlan(organizationId, payload.id));
  const expected = payload.intervalChangeExpectedState;
  const attemptedAt = Date.now();
  const attemptId = `${payload.id}-${attemptedAt}`;

  return firestore().runTransaction(async (transaction) => {
    const snapshot = await transaction.get(planRef);
    if (!snapshot.exists) throw new InvoicePlanSaveError('plan_not_found');

    const current = snapshot.data() ?? {};
    const recovery = payload.intervalTransitionRecovery;
    if (
      !financialExpectedFieldMatches(current, expected, 'invoiceItemList') ||
      !financialExpectedFieldMatches(current, expected, 'total')
    ) {
      throw new InvoicePlanSaveError('stale');
    }
    const changedField = INTERVAL_SCHEDULER_FIELDS.find(
      (field) =>
        !(
          recovery &&
          field === 'intervalTransitionRecovery' &&
          recoveryValuesEqualIgnoringSupportAlert(current[field] ?? null, expected[field] ?? null)
        ) && !schedulerValuesEqual(current[field] ?? null, expected[field] ?? null)
    );
    if (changedField) {
      throw new InvoicePlanSaveError('stale');
    }

    if (recovery) {
      assertRecoveryIsEligible(current, recovery);
    } else {
      assertNewIntervalChangeIsEligible(current, payload);
    }

    const notificationDays = normalizeNotificationDays(current.notificationDays ?? 1);
    const locationId = resolveInvoicePlanLocationId(current.location, current.students?.[0] ?? payload.students?.[0]);
    if (!locationId) throw new InvoicePlanSaveError('location_missing');
    if (payload.location !== locationId) {
      throw new InvoicePlanSaveError('location_stale');
    }
    const locationSnapshot = await transaction.get(
      firestore().doc(`organizations/${organizationId}/locations/${locationId}`)
    );
    const timezone = locationSnapshot.exists ? locationSnapshot.data()?.timezone : null;
    if (!timezone || !moment.tz.zone(timezone)) throw new InvoicePlanSaveError('location_invalid');
    let dateSend = current.dateSend;
    if (!recovery) {
      const submittedNotificationDays = normalizeNotificationDays(
        payload.notificationDays ?? current.notificationDays ?? 1
      );
      dateSend = moment(current.dateDue).tz(timezone).subtract(submittedNotificationDays, 'days').valueOf();
      if (!isFiniteTimestamp(dateSend) || dateSend <= attemptedAt) {
        throw new InvoicePlanSaveError('send_elapsed');
      }
    }
    const desiredServiceMode = recovery
      ? { serviceDirection: current.serviceDirection, isServiceDatesEnabled: current.isServiceDatesEnabled }
      : getDesiredServiceMode(current, payload);
    const serviceDirection = desiredServiceMode.serviceDirection;
    const isServiceDatesEnabled = desiredServiceMode.isServiceDatesEnabled;
    const dateEnd = recovery
      ? (current.dateEnd ?? null)
      : Object.prototype.hasOwnProperty.call(payload, 'dateEnd')
        ? payload.dateEnd
        : (current.dateEnd ?? null);
    assertFutureFullPeriodIsEligible(current, desiredServiceMode, dateEnd, timezone);
    transaction.update(planRef, {
      billingInterval: recovery ? current.billingInterval : payload.billingInterval,
      location: locationId,
      invoiceItemList,
      total: recomputedTotal,
      dateEnd,
      dateSend,
      notificationDays: recovery
        ? notificationDays
        : normalizeNotificationDays(payload.notificationDays ?? current.notificationDays ?? 1),
      serviceDirection,
      isServiceDatesEnabled,
      updatedAt: attemptedAt,
      updatedBy: payload.updatedBy,
      status: recovery ? 'scheduled' : expected.status,
      ...(recovery ? { generationLeaseExpiresAt: null } : {}),
      intervalChangeEffectiveDue: recovery?.effectiveDue ?? expected.dateDue,
      intervalChangePreviousBillingInterval: recovery?.previousBillingInterval ?? expected.billingInterval,
      intervalChangeNotificationPending: null,
      intervalTransitionAttemptId: recovery?.attemptId ?? attemptId,
      intervalTransitionPhase: 'awaiting_first_invoice',
      intervalTransitionError: null,
      intervalTransitionRecovery: null,
      intervalTransitionRecoveryHistory: recovery
        ? [...(current.intervalTransitionRecoveryHistory ?? []), current.intervalTransitionRecovery].slice(
            -INTERVAL_TRANSITION_RECOVERY_HISTORY_LIMIT
          )
        : (current.intervalTransitionRecoveryHistory ?? null),
      servicePeriodCalculationMode: 'due-to-due',
    });
  });
}

function validateIntervalTransitionFinancialPayload(invoiceItemList, submittedTotal) {
  try {
    if (!Array.isArray(invoiceItemList) || validateCadenceTransitionFinancials(invoiceItemList)) {
      throw new Error('Invalid recurring financial row.');
    }
    const recomputedTotal = calculateCadenceTransitionTotal(invoiceItemList);
    if (dollarsToCents(submittedTotal) !== dollarsToCents(recomputedTotal)) {
      throw new Error('Submitted recurring total does not match its rows.');
    }
    return recomputedTotal;
  } catch {
    throw new InvoicePlanSaveError('financial_invalid');
  }
}

function assertNewIntervalChangeIsEligible(current, payload) {
  if (current.status !== 'active') throw new InvoicePlanSaveError('plan_not_active');
  if (current.intervalChangePreviousBillingInterval || current.intervalTransitionRecovery) {
    throw new InvoicePlanSaveError('pending_transition');
  }
  const previous = String(current.billingInterval ?? '').toLowerCase();
  const next = String(payload.billingInterval ?? '').toLowerCase();
  if (
    !SUPPORTED_BILLING_INTERVALS.has(previous) ||
    !SUPPORTED_BILLING_INTERVALS.has(next) ||
    previous === next ||
    String(payload.intervalChangePreviousBillingInterval ?? '').toLowerCase() !== previous ||
    String(payload.oldBillingInterval ?? '').toLowerCase() !== previous
  ) {
    throw new InvoicePlanSaveError('interval_invalid');
  }
  if (!isFiniteTimestamp(current.dateDue) || payload.intervalChangeEffectiveDue !== current.dateDue) {
    throw new InvoicePlanSaveError('due_invalid');
  }
  if (!isFiniteTimestamp(current.dateSend) || current.dateSend <= Date.now()) {
    throw new InvoicePlanSaveError('send_elapsed');
  }
  getDesiredServiceMode(current, payload);
}

function assertRecoveryIsEligible(current, recovery) {
  const snapshot = recovery.schedulerSnapshot;
  if (current.status !== 'paused') throw new InvoicePlanSaveError('recovery_not_paused');
  if (
    !current.intervalTransitionRecovery ||
    recovery.attemptId !== current.intervalTransitionAttemptId ||
    recovery.attemptId !== current.intervalTransitionRecovery.attemptId ||
    current.billingInterval !== recovery.nextBillingInterval ||
    current.intervalChangePreviousBillingInterval !== recovery.previousBillingInterval ||
    current.intervalChangeEffectiveDue !== recovery.effectiveDue ||
    current.dateDue !== recovery.effectiveDue ||
    current.intervalChangeNotificationPending === true ||
    current.intervalTransitionPhase !== 'awaiting_first_invoice' ||
    !snapshot ||
    !schedulerValuesEqual(current.dateDue, snapshot.dateDue) ||
    !schedulerValuesEqual(current.dateSend, snapshot.dateSend) ||
    !schedulerValuesEqual(current.dateServiceStart ?? null, snapshot.dateServiceStart ?? null) ||
    !schedulerValuesEqual(current.dateServiceEnd ?? null, snapshot.dateServiceEnd ?? null) ||
    !schedulerValuesEqual(current.location ?? null, snapshot.location ?? null) ||
    snapshot.intervalTransitionPhase !== 'awaiting_first_invoice' ||
    !isFiniteTimestamp(recovery.effectiveDue)
  ) {
    throw new InvoicePlanSaveError('recovery_stale');
  }
  assertServiceBoundary(current);
}

function getDesiredServiceMode(current, payload) {
  if (typeof payload.isServiceDatesEnabled !== 'boolean') {
    throw new InvoicePlanSaveError('service_mode_invalid');
  }
  if (!payload.isServiceDatesEnabled) {
    return { isServiceDatesEnabled: false, serviceDirection: null };
  }
  if (String(payload.serviceDirection).toLowerCase() !== 'next') {
    throw new InvoicePlanSaveError('service_direction_invalid');
  }
  if (!isFiniteTimestamp(current.dateServiceEnd)) {
    throw new InvoicePlanSaveError('service_boundary_invalid');
  }
  return { isServiceDatesEnabled: true, serviceDirection: 'next' };
}

function assertFutureFullPeriodIsEligible(current, desiredServiceMode, dateEnd, timezone) {
  if (dateEnd == null) return;
  if (!isFiniteTimestamp(dateEnd)) throw new InvoicePlanSaveError('end_invalid');
  if (current.dateDue > dateEnd) {
    throw new InvoicePlanSaveError('no_future_period');
  }
  const boundary = desiredServiceMode.isServiceDatesEnabled ? current.dateServiceEnd : current.dateDue;
  const candidateStart = desiredServiceMode.isServiceDatesEnabled
    ? moment(boundary).tz(timezone).startOf('day').add(1, 'day').valueOf()
    : moment(boundary).tz(timezone).add(1, 'day').valueOf();
  if (!isFiniteTimestamp(candidateStart) || candidateStart >= dateEnd) {
    throw new InvoicePlanSaveError('no_future_period');
  }
}

function assertServiceBoundary(plan) {
  if (!plan.isServiceDatesEnabled) return;
  if (String(plan.serviceDirection).toLowerCase() !== 'next' || !isFiniteTimestamp(plan.dateServiceEnd)) {
    throw new InvoicePlanSaveError('service_boundary_invalid');
  }
}

function isFiniteTimestamp(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function normalizeNotificationDays(value) {
  const days = typeof value === 'string' ? Number.parseInt(value, 10) : value;
  if (!Number.isInteger(days) || days < 0) throw new InvoicePlanSaveError('notification_invalid');
  return days;
}

function schedulerValuesEqual(currentValue, expectedValue) {
  if (Object.is(currentValue, expectedValue)) return true;
  if (!currentValue || !expectedValue || typeof currentValue !== 'object' || typeof expectedValue !== 'object') {
    return false;
  }
  return JSON.stringify(currentValue) === JSON.stringify(expectedValue);
}

function financialExpectedFieldMatches(current, expected, field) {
  const currentHasField = Object.prototype.hasOwnProperty.call(current, field);
  const expectedHasField = Object.prototype.hasOwnProperty.call(expected, field);
  return currentHasField === expectedHasField && isEqual(current[field], expected[field]);
}

function recoveryValuesEqualIgnoringSupportAlert(currentRecovery, expectedRecovery) {
  if (!currentRecovery || !expectedRecovery) return currentRecovery === expectedRecovery;
  const { supportAlert: _currentSupportAlert, ...currentIdentity } = currentRecovery;
  const { supportAlert: _expectedSupportAlert, ...expectedIdentity } = expectedRecovery;
  return schedulerValuesEqual(currentIdentity, expectedIdentity);
}

function prepareInvoicePlanForWrite(invoicePlan) {
  const {
    id,
    uid,
    intervalChangeExpectedState,
    intervalChangeServicePeriodPreview,
    intervalChangePreviewServiceStart,
    intervalChangePreviewServiceEnd,
    oldBillingInterval,
    ...strippedInvoicePlan
  } = invoicePlan;
  void intervalChangeExpectedState;
  void intervalChangeServicePeriodPreview;
  void intervalChangePreviewServiceStart;
  void intervalChangePreviewServiceEnd;
  void oldBillingInterval;
  const locationId = invoicePlan?.students?.length ? findLocationIdForStudent(invoicePlan.students[0]) : '';
  const data = {
    ...strippedInvoicePlan,
    isInvoice: false,
    location: locationId,
    students: invoicePlan?.students ?? [],
    invoiceItemList: invoicePlan?.invoiceItems ?? invoicePlan.invoiceItemList ?? [],
    updatedAt: Date.now(),
  };

  if (!data.createdAt) data.createdAt = data.updatedAt;
  return data;
}

export async function updateOrganizationInvoice(organizationId, invoice) {
  if (!invoice?.id) throw Error('Invoice ID is required');

  const { id, ...data } = invoice;

  await setDocument({
    path: paths.invoice(organizationId, id),
    data,
  });
}
export async function removeOrganizationInvoice(organizationId, invoiceId) {
  await deleteDocument({
    path: paths.invoice(organizationId, invoiceId),
  });
}

export async function resendInvoiceToStripe(invoice) {
  const data = {
    status: InvoiceStatus.DRAFT,
    paidAmount: 0,
    //payments: [],
    stripe: 'null',
  };
  const path = paths.invoice(invoice.organization, invoice.id);
  await updateDocument({ path, data });
  data.status = InvoiceStatus.OPEN;
  await updateDocument({ path, data });
}

export const refreshInvoiceLinks = (stripeInvoiceId) =>
  functions().httpsCallable('callables-invoices-refreshInvoiceLinks')({
    stripeInvoiceId,
  });

export async function markInvoicesAsArchive(organizationId, invoices) {
  for (const invoice of invoices) {
    const data = {
      isArchived: true,
    };

    const path = paths.invoice(organizationId, invoice.id);
    await updateDocument({ path, data });
  }
}

export async function markRecurringPlansAsArchive(organizationId, plans) {
  for (const plan of plans) {
    const data = {
      isArchived: true,
    };

    const path = paths.invoicePlan(organizationId, plan.id);
    await updateDocument({ path, data });
  }
}

export const updateInvoiceStatus = async (stripeInvoiceId, status) => {
  const { data } = await functions().httpsCallable('callables-stripe-updateInvoiceStatus')({ stripeInvoiceId, status });
  return data;
};

export const createRefundForInvoice = async ({ invoiceId, organizationId, reason = '' }) => {
  const { data } = await functions().httpsCallable('callables-stripe-createRefundForInvoice')({
    invoiceId,
    organizationId,
    reason,
  });
  return data;
};
